import { type FieldError as ApiFieldError, isApiError } from '@saay/api-client';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useId, useRef, useState } from 'react';

import type {
  PhysicalCardNumberingPolicyVersion,
  PolicyVersion,
  PolicyVersionCreate,
} from '../api.js';
import { useSubmitBlocked } from '../components/form.js';
import {
  Banner,
  Button,
  CommandFeedback,
  Dialog,
  FieldErrorList,
  TextField,
  toFormPath,
  useToast,
} from '../kit.js';
import { NUMBERING_POLICY_TYPE } from '../permissions.js';
import { setupKeys, useCurrentPolicy } from '../queries.js';
import {
  type NumberingChange,
  toNumberingVersionCreate,
  valuesOf,
} from './physical-card-config.js';

/**
 * Saving one row action of «کارت‌های فیزیکی» (D-117, AC §31.6): the change is applied to the version
 * in force and the whole configuration is posted with `expectedVersion` and the reason. One
 * `useCommand` per open dialog or editor (one intent, one `Idempotency-Key`; an edited body is a new
 * intent). No optimistic success: the tables refresh from the server after a confirmed `201`.
 *
 * - `409 RESOURCE_VERSION_CONFLICT`: the version and the tables are read again and one line says that
 *   someone else changed it; «ذخیره» again builds on the new version.
 * - The lock codes show their `messageFa` in the dialog or the editor.
 * - `400 REQUEST_INVALID`: the field errors of the edited row land on its fields (`fields` maps the
 *   server path, for example `cities.12.cityCode`, to the field); the rest are listed.
 */

type Version = PhysicalCardNumberingPolicyVersion;

export const LOCK_CODES: ReadonlySet<string> = new Set([
  'PHYSICAL_CARD_SERIES_IN_USE',
  'PHYSICAL_CARD_PROVINCE_IN_USE',
  'PHYSICAL_CARD_CITY_IN_USE',
]);

export const CONFLICT_LINE =
  'در این فاصله کس دیگری تنظیمات را تغییر داد؛ اطلاعات به‌روز شد. دوباره ذخیره کنید.';

function isNumberingVersion(version: PolicyVersion | undefined): version is Version {
  return version?.policyType === NUMBERING_POLICY_TYPE;
}

/** The version in force (`expectedVersion` is its number: this type has no scheduled version). */
export function useNumberingVersion() {
  const current = useCurrentPolicy(NUMBERING_POLICY_TYPE);
  return {
    query: current,
    version: isNumberingVersion(current.data) ? current.data : undefined,
  };
}

export interface SaveRequest {
  readonly change: NumberingChange;
  readonly reason: string;
  /** Server field paths (form notation) of the edited row, mapped to the dialog's fields. */
  readonly fields?: (values: ReturnType<typeof valuesOf>) => Readonly<Record<string, string>>;
  readonly setError?: (field: string, message: string) => void;
}

export interface NumberingSave {
  readonly blocked: boolean;
  readonly checkStatus: () => void;
  /** Set while the last answer was a conflict or a lock refusal: one line in the dialog. */
  readonly problem: string | undefined;
  readonly pending: boolean;
  readonly ready: boolean;
  readonly reset: () => void;
  readonly save: (request: SaveRequest) => Promise<boolean>;
  /** `CommandFeedback`'s state, without the answers shown here already. */
  readonly feedback: Parameters<typeof CommandFeedback>[0]['state'];
  readonly unmapped: readonly ApiFieldError[];
  readonly version: Version | undefined;
}

export function useNumberingSave(name: string, onSaved?: () => void): NumberingSave {
  const queryClient = useQueryClient();
  const toast = useToast();
  const offline = useSubmitBlocked();
  const { version } = useNumberingVersion();
  const [problem, setProblem] = useState<string | undefined>();
  const [unmapped, setUnmapped] = useState<readonly ApiFieldError[]>([]);
  const last = useRef<{ fields: Readonly<Record<string, string>>; request: SaveRequest } | null>(
    null,
  );

  function refresh() {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: setupKeys.policy(NUMBERING_POLICY_TYPE) }),
      queryClient.invalidateQueries({ queryKey: setupKeys.numbering }),
    ]);
  }

  const command = useCommand<PolicyVersionCreate, PolicyVersion>(
    `policy.${NUMBERING_POLICY_TYPE}.${name}`,
    (client, body, intent) =>
      client.post(`/admin/policies/${NUMBERING_POLICY_TYPE}/versions`, { body, intent }),
    {
      onSuccess: async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          description: 'از همین حالا اعمال می‌شود؛ شماره کارت‌های صادرشده عوض نمی‌شود.',
          title: 'ذخیره شد',
        });
        onSaved?.();
      },
    },
  );

  function handle(outcome: CommandOutcome<PolicyVersion>): boolean {
    if (outcome.status === 'success') return true;
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return false;
    const error = outcome.error;
    if (error.code === 'RESOURCE_VERSION_CONFLICT') {
      setProblem(CONFLICT_LINE);
      void refresh();
    } else if (LOCK_CODES.has(error.code)) {
      setProblem(error.messageFa);
    } else if (error.code === 'REQUEST_INVALID') {
      const fields = last.current?.fields ?? {};
      const setError = last.current?.request.setError;
      const left: ApiFieldError[] = [];
      for (const fieldError of error.fieldErrors) {
        const target = fields[toFormPath(fieldError.field)];
        if (target !== undefined && setError !== undefined) {
          setError(target, fieldError.messageFa);
        } else {
          left.push(fieldError);
        }
      }
      setUnmapped(left);
    }
    return false;
  }

  async function save(request: SaveRequest): Promise<boolean> {
    setProblem(undefined);
    setUnmapped([]);
    if (version === undefined) return false;
    const values = request.change(valuesOf(version));
    last.current = { fields: request.fields?.(values) ?? {}, request };
    return handle(
      await command.submit(toNumberingVersionCreate(values, version.version, request.reason)),
    );
  }

  const state = command.state;
  const handled =
    state.status === 'error' &&
    isApiError(state.error) &&
    (state.error.code === 'RESOURCE_VERSION_CONFLICT' ||
      state.error.code === 'REQUEST_INVALID' ||
      LOCK_CODES.has(state.error.code));
  return {
    blocked: offline.blocked,
    checkStatus: () => void command.checkStatus().then(handle),
    feedback: handled ? { ...state, status: 'idle' } : state,
    pending: state.status === 'pending',
    problem,
    ready: version !== undefined,
    reset: () => {
      setProblem(undefined);
      setUnmapped([]);
      command.reset();
    },
    save,
    unmapped,
    version,
  };
}

// ---------------------------------------------------------------------------------------------
// The reason: a compact required field with chips; the first chip is already chosen.

export function reasonProblem(reason: string): string | undefined {
  const text = reason.trim();
  if (text === '') return 'دلیل را بنویسید یا یکی را انتخاب کنید.';
  if (text.length > 500) return 'دلیل بلندتر از حد مجاز است.';
  return undefined;
}

export function ReasonPicker({
  error,
  onChange,
  presets,
  value,
}: {
  readonly error?: string | undefined;
  readonly onChange: (value: string) => void;
  readonly presets: readonly string[];
  readonly value: string;
}) {
  const labelId = useId();
  return (
    <div className="flex flex-col gap-2">
      <TextField
        error={error}
        label="دلیل"
        maxLength={500}
        onChange={(event) => onChange(event.target.value)}
        value={value}
      />
      <div aria-labelledby={labelId} className="flex flex-wrap gap-1.5" role="group">
        <span className="sr-only" id={labelId}>
          دلیل‌های آماده
        </span>
        {presets.map((preset) => {
          const chosen = value.trim() === preset;
          return (
            <button
              aria-pressed={chosen}
              className={
                chosen
                  ? 'h-7 rounded-full bg-primary/10 px-3 text-xs font-medium text-primary'
                  : 'h-7 rounded-full bg-muted px-3 text-xs text-muted-foreground hover:text-foreground'
              }
              key={preset}
              onClick={() => onChange(preset)}
              type="button"
            >
              {preset}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** The one line of a conflict or a lock refusal. */
export function ProblemLine({ message }: { readonly message: string | undefined }) {
  return message === undefined ? null : (
    <p className="text-sm text-destructive" role="alert">
      {message}
    </p>
  );
}

/**
 * The frame of every small dialog of the numbering tables: a title, the fields, the reason, the
 * server's answer, and «انصراف» / the submit button (validation on submit).
 */
export function ChangeDialog({
  children,
  description,
  destructive = false,
  onOpenChange,
  onSubmit,
  open,
  reason,
  save,
  submitLabel = 'ذخیره',
  title,
}: {
  readonly children?: ReactNode;
  readonly description?: ReactNode;
  readonly destructive?: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmit: () => void;
  readonly open: boolean;
  readonly reason: ReactNode;
  readonly save: NumberingSave;
  readonly submitLabel?: string;
  readonly title: string;
}) {
  return (
    <Dialog
      {...(description === undefined ? {} : { description })}
      onOpenChange={onOpenChange}
      open={open}
      title={title}
    >
      <form
        aria-label={title}
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        {children}
        {reason}
        <ProblemLine message={save.problem} />
        <FieldErrorList errors={save.unmapped} />
        <CommandFeedback onCheckStatus={save.checkStatus} state={save.feedback} />
        {save.ready ? null : <Banner tone="info">در حال خواندن تنظیمات فعلی…</Banner>}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button onClick={() => onOpenChange(false)} type="button" variant="secondary">
            انصراف
          </Button>
          <Button
            disabled={save.blocked || !save.ready}
            loading={save.pending}
            type="submit"
            variant={destructive ? 'danger' : 'primary'}
          >
            {submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
